import {
  ArcgisEndpointConstant,
  IUbigeoRepository,
  UbigeoEntity,
  ResponseValueObject,
} from '@gishub/domain';
import axios from 'axios';

/**
 * Repositorio de ubigeo (Departamento → Provincia → Distrito) basado en el
 * servicio SERV_LIMITE_CENSAL (winlmprap14) — la misma fuente que usa el TOC
 * para los límites políticos y el KPI para los límites administrativos.
 *
 * Subcapas y campos INEI (única fuente: `ArcgisEndpointConstant.LIMITE_FIELDS`):
 *   0 → Departamento  (CODDEP 2, NOMBDEP)
 *   1 → Provincia     (CODPROV 4, NOMBPROV)
 *   2 → Distrito      (UBIGEO  6, NOMBDIST; componentes CODDEP/CODPROV de 2)
 *
 * Los códigos conservan el largo jerárquico 2/4/6 con el que el visor deduce el
 * nivel territorial (ver `fire-scope.ts`), así que son intercambiables con los
 * del servicio anterior (SAC/SAC: IDDPTO/IDPROV/IDDIST).
 */
export class UbigeoRepository implements IUbigeoRepository {
  /** Servicio de límites censales — fuente oficial, de acceso público. */
  private readonly baseUrl = ArcgisEndpointConstant.LIMITE_CENSAL_MAPSERVER;

  /** Subcapa de departamentos. */
  private readonly DEP_LAYER = ArcgisEndpointConstant.LIMITE_DEPARTAMENTO.sublayer;
  /** Subcapa de provincias. */
  private readonly PROV_LAYER = ArcgisEndpointConstant.LIMITE_PROVINCIAL.sublayer;
  /** Subcapa de distritos. */
  private readonly DIST_LAYER = ArcgisEndpointConstant.LIMITE_DISTRITAL.sublayer;

  /** Campos por nivel (código + nombre). */
  private readonly F_DEP = ArcgisEndpointConstant.LIMITE_FIELDS.DEPARTAMENTO;
  private readonly F_PROV = ArcgisEndpointConstant.LIMITE_FIELDS.PROVINCIA;
  private readonly F_DIST = ArcgisEndpointConstant.LIMITE_FIELDS.DISTRITO;

  static readonly DEFAULT_DEPARTMENT_CODE = '12'; // Junín

  async getDepartments(): Promise<ResponseValueObject<UbigeoEntity[]>> {
    const response = await axios.get(`${this.baseUrl}/${this.DEP_LAYER}/query`, {
      params: {
        where: '1=1',
        outFields: `${this.F_DEP.code},${this.F_DEP.name}`,
        orderByFields: `${this.F_DEP.name} ASC`,
        f: 'json',
        returnGeometry: false,
      },
    });
    const entities: UbigeoEntity[] = (response.data.features || [])
      .map((f: any) => ({
        code: f.attributes[this.F_DEP.code],
        name: f.attributes[this.F_DEP.name],
        parentCode: null,
      }))
      .filter((e: UbigeoEntity) => e.code !== null && e.name !== null);
    return new ResponseValueObject(true, entities);
  }

  async getProvincesByDepartment(
    departmentCode: string,
  ): Promise<ResponseValueObject<UbigeoEntity[]>> {
    const response = await axios.get(`${this.baseUrl}/${this.PROV_LAYER}/query`, {
      params: {
        where: `${this.F_PROV.departmentField} = '${departmentCode}'`,
        outFields: `${this.F_PROV.departmentField},${this.F_PROV.code},${this.F_PROV.name}`,
        orderByFields: `${this.F_PROV.name} ASC`,
        f: 'json',
        returnGeometry: false,
      },
    });
    const entities: UbigeoEntity[] = (response.data.features || []).map(
      (f: any) => ({
        code: f.attributes[this.F_PROV.code],
        name: f.attributes[this.F_PROV.name],
        parentCode: f.attributes[this.F_PROV.departmentField],
      }),
    );
    return new ResponseValueObject(true, entities);
  }

  async getDistrictsByProvince(
    provinceCode: string,
  ): Promise<ResponseValueObject<UbigeoEntity[]>> {
    // El código de provincia es de 4 dígitos (dept + prov); en la capa de
    // distritos esos componentes vienen separados, cada uno de 2 dígitos.
    const departmentCode = provinceCode.slice(0, 2);
    const provincePart = provinceCode.slice(2, 4);
    const response = await axios.get(`${this.baseUrl}/${this.DIST_LAYER}/query`, {
      params: {
        where: `${this.F_DIST.departmentField} = '${departmentCode}' AND ${this.F_DIST.provinceField} = '${provincePart}'`,
        outFields: `${this.F_DIST.code},${this.F_DIST.name}`,
        orderByFields: `${this.F_DIST.name} ASC`,
        f: 'json',
        returnGeometry: false,
      },
    });
    const entities: UbigeoEntity[] = (response.data.features || []).map(
      (f: any) => ({
        code: f.attributes[this.F_DIST.code],
        name: f.attributes[this.F_DIST.name],
        parentCode: provinceCode,
      }),
    );
    return new ResponseValueObject(true, entities);
  }

  getDefaultDepartmentCode(): string {
    return UbigeoRepository.DEFAULT_DEPARTMENT_CODE;
  }

  async getDepartmentGeometry(
    departmentCode: string,
  ): Promise<ResponseValueObject<any>> {
    return this.queryGeometry(this.DEP_LAYER, this.F_DEP.code, departmentCode);
  }

  async getProvinceGeometry(
    provinceCode: string,
  ): Promise<ResponseValueObject<any>> {
    return this.queryGeometry(this.PROV_LAYER, this.F_PROV.code, provinceCode);
  }

  async getDistrictGeometry(
    districtCode: string,
  ): Promise<ResponseValueObject<any>> {
    return this.queryGeometry(this.DIST_LAYER, this.F_DIST.code, districtCode);
  }

  /** Geometría del polígono que coincide con `code` en la subcapa indicada. */
  private async queryGeometry(
    layer: number,
    codeField: string,
    code: string,
  ): Promise<ResponseValueObject<any>> {
    const response = await axios.get(`${this.baseUrl}/${layer}/query`, {
      params: {
        where: `${codeField} = '${code}'`,
        outFields: '*',
        f: 'json',
        returnGeometry: true,
      },
    });
    return new ResponseValueObject(true, response.data);
  }
}
